import React, { useRef, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useMediaPredicate } from 'react-media-hook'

import { WidgetLayout } from '../../../lib/widget-layout'
import { OcppFilterButton } from '../../../lib/ocpp-filter-button'
import { useChargePointGetOcppRelayLogsQuery } from '../../redux/api/web-api'
import { LOG_TYPES_PARTNER, LOG_TYPES_SUPPORT, LogTypes } from '../../../common/constants/LogTypes'
import { WidgetDropdownFilter } from '../../../lib/widget-dropdown-filter'
import { isMoreItemsToLoad } from '../../../lib/is-more-items-to-load'
import { LogsTable, LOGS_NO_OF_ITEMS } from '../../../lib/logs-table'
import Text from '../../../ui/Text'
import { isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../hooks/useText'
import { IconButton } from '../charge-point-ocpp-logs-widget'
import { RefreshIcon } from '../../icons/refresh-icon'

export type ChargePointOcppRelayLogsWidgetProps = {
    className?: ClassName
    chargePointId: string
    style?: React.CSSProperties
    showLoadmoreButton?: boolean
    showExpandIconForLogs?: boolean
}
const DEFAULT_UNSELECTED_FILTERS: Array<LogTypes> = isSupportDomain
    ? [LogTypes.Heartbeat, LogTypes.MeterValues]
    : [LogTypes.StatusNotification]

const SRelayBadge = styled.div`
    background-color: ${({ theme }) => theme.brandColors.CA_Blue};
    color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
    margin-left: 5px;
    font-size: 14px;
    font-weight: 400;
    border-radius: 4px;
    padding: 6px 4px;
    text-transform: initial;
    font-family: ${({ theme }) => theme.font.name};
`
export const ChargePointOcppRelayLogsWidget = React.memo(function ChargePointOcppRelayLogsWidget({
    className,
    chargePointId,
    style,
}: ChargePointOcppRelayLogsWidgetProps): React.ReactElement {
    const text = useText()
    const isMobileScreen = useMediaPredicate('(max-width: 576px)')

    const [filters, setFilters] = useState(() => {
        const logTypes = isSupportDomain ? LOG_TYPES_SUPPORT : LOG_TYPES_PARTNER

        return logTypes.filter((type) => !DEFAULT_UNSELECTED_FILTERS.includes(type))
    })

    const [noOfItems, setNoOfItems] = useState(LOGS_NO_OF_ITEMS)
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)
    const filterButtonRef = useRef<HTMLButtonElement | null>(null)
    const {
        data: logs = [],
        refetch,
        isLoading,
        isFetching,
    } = useChargePointGetOcppRelayLogsQuery({ chargePointId, noOfItems, actions: filters.map((i) => LogTypes[i]) })

    const onFilterChanged = (filters: Array<LogTypes>) => {
        setFilters(filters)
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOcppRelayLogsWidget')}
            loadingStatus={isLoading || isFetching}
            headerInfoIcon={!isMobileScreen && <SRelayBadge>{text('lblRelayEnabled')}</SRelayBadge>}
            headerText={isMobileScreen && <SRelayBadge>{text('lblRelayEnabled')}</SRelayBadge>}
            headerButtons={
                <div className="flex gap-3">
                    <IconButton onClick={() => refetch()} data-testid="ocpp-relay-refresh-button">
                        <RefreshIcon />
                    </IconButton>

                    <OcppFilterButton
                        isDropdownOpened={isFilterModalVisible}
                        numberOfSelectedFilters={isFilterModalVisible ? 0 : filters.length}
                        action={() => setIsFilterModalVisible(!isFilterModalVisible)}
                        reference={filterButtonRef}
                        data-testid="ocpp-relay-filter-button"
                    />
                </div>
            }
            data-testid="ocpp-relay-logs"
        >
            {isFilterModalVisible && filters && (
                <WidgetDropdownFilter
                    onChange={onFilterChanged}
                    options={filters}
                    defaultOptions={LOG_TYPES_SUPPORT}
                    onClose={() => setIsFilterModalVisible(false)}
                    filterButtonRef={filterButtonRef}
                />
            )}
            <LogsTable
                columns={[
                    { label: text('lblTimestamp'), accessor: 'timestamp', width: 100 },
                    { label: text('lblAction'), accessor: 'action' },
                    { label: text('lblMessage'), accessor: 'message' },
                ]}
                logs={logs}
                actions={
                    isMoreItemsToLoad(noOfItems, logs.length)
                        ? [
                              {
                                  title: text('btnLoadMore'),
                                  onClick: () => setNoOfItems((curr) => curr + logs.length),
                              },
                          ]
                        : []
                }
                loading={isLoading || isFetching}
            />

            {!isLoading &&
                !logs.length &&
                (!filters.length ? (
                    <Text code="msgSelectFiltersToViewLogs" />
                ) : (
                    <Text code="msgNoLogsForSelectedActionTypes" />
                ))}
        </WidgetLayout>
    )
})
